import React, { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import DropdownButton from "react-bootstrap/DropdownButton"
import Dropdown from "react-bootstrap/Dropdown"
import PlanningModeButtons from "./PlanningModeButtons"
import "./index.scss"

import Button from "react-bootstrap/Button"
import { HelpButton, Popup, UpdateIndicator, SimulationLogoutConfirm } from "components"
import { MemberConfig } from "components/Config"
import DateButtons from "./DateButtons"

import { visual, teamStatus, auth } from "state_management"
import { DateTime } from "luxon"

import { FiSettings, FiMenu, FiHome, FiLogOut } from "react-icons/fi"
import { RiUserSettingsLine } from "react-icons/ri"
import { TbArrowsMaximize, TbArrowsMinimize } from "react-icons/tb";
import isOnMobile from "utils/scripts/isOnMobile"
import useT from "utils/hooks/useT"


const SCREEN_TEAM_PLAN = "team-plan"
const SCREEN_SCRUM_VIEW = "scrum-view"
const SCREEN_ASSET_VIEW = "asset-view"

export const setMondayDate = (date, mondayDate, dispatch) => {
  const thisMonday = date.startOf("week")
  if (!mondayDate.hasSame(thisMonday, "day")) {
    dispatch(visual.changeMondayDate(thisMonday.toISO()))
  }
}

// Helper function to map screen types to screen keys
const getScreenKey = (type) => {
  switch (type) {
    case SCREEN_TEAM_PLAN:
      return "teamPlan"
    case SCREEN_ASSET_VIEW:
      return "assetView"
    case "single-member":
      return "singleMember"
    default:
      return null
  }
}
// Buttons used on various schedule screens.
export default function ScheduleButtons({ type, member, moveLeft, moveRight, view }) {
  const dispatch = useDispatch()
  const { t } = useT("common")
  const location = useLocation()
  const navigate = useNavigate()
  const mondayDate = DateTime.fromISO(useSelector((state) => state.visual.scheduleMondayDate))
  const activeDate = DateTime.fromISO(useSelector((state) => state.visual.activeDate))
  const showWeekendAssetView = useSelector((state) => state.visual.assetView.showWeekend)
  const isInPlanningMode = useSelector((state) => state.teamStatus.isInPlanningMode)
  const isCurrentPlanner = useSelector((state) => state.teamStatus.isCurrentPlanner)
  const isMember = useSelector((state) => state.auth.isMember)
  const memberId = useSelector((state) => state.auth.memberId)
  const showProjects = useSelector((state) => state.teamStatus.showProjects)
  const showAnalyses = useSelector((state) => state.teamStatus.showAnalyses)
  const showLogoutConfirm = useSelector((state) => state.teamStatus.showLogoutConfirm)
  const directToMemberPlan = useSelector((state) => state.teamStatus.directToMemberPlan)
  const showLibrarian = useSelector((state) => state.teamStatus.showLibrarian)
  const isPlanner = useSelector((state) => state.auth.role) === "planner"
  const isViewOnly = useSelector((state) => state.auth.isViewOnly)
  const allowTaskTransfer = useSelector((state) => state.teamStatus.allowTaskTransfer)
  const currentMemberShowOnPlan = useSelector((state) => memberId && state.members[memberId] ? state.members[memberId].showOnPlan : true)

  const [activeConfig, setActiveConfig] = useState(null)
  
  // Get screen key for this component
  const screenKey = getScreenKey(type)
  
  // Use screen-specific settings for supported screens
  const withWeekend = useSelector((state) => 
    screenKey ? state.visual.screens[screenKey].scheduleWithWeekend : state.visual.scheduleWithWeekend
  )
  const withWeek = useSelector((state) => 
    screenKey ? state.visual.screens[screenKey].scheduleWithWeek : state.visual.scheduleWithWeek
  )
  const showCompact = useSelector((state) => 
    screenKey ? state.visual.screens[screenKey].showCompact : state.visual.showCompact
  )


  const isDayView = view === "day-view"
  let dropDownOptions = []
  dropDownOptions.push({ url: "/", name: t("teamPlan") })
  dropDownOptions.push({ url: "/asset-view", name: t("instrumentView") })
  if (showAnalyses) {
    dropDownOptions.push({ url: "/sample-view", name: t("sampleView") })
  }
  if (showProjects) {
    dropDownOptions.push({ url: "/project-view", name: t("projectView") })
  }
  //if (showProjects) {
  //  dropDownOptions.push({ url: "/scrum-view", name: "Scrum View" })
  //}
  if (currentMemberShowOnPlan) {
    dropDownOptions.push({ url: `/member/${memberId}`, name: t("memberPlan") })
  }
  if (isPlanner) {
    dropDownOptions.push({ url: "/setup/members", name: t("settings") })
  }
  const isCreatingProject = location.pathname === "/setup/projects/create"
  const isEditingProject = location.pathname !== "/setup/projects/create" && location.pathname.includes("/setup/projects/")

  const isStandalone = () => {
    return (window.matchMedia('(display-mode: standalone)').matches)
  }

  const handleLibrarianNavigation = () => {
    if (isStandalone()) {
      navigate('/librarian')
    } else {
      window.open('/librarian', '_blank')
    }
  }



  return (
    <>
      {showLogoutConfirm && <SimulationLogoutConfirm/>}

      {activeConfig && (
        <Popup nonScrollable onCancel={() => setActiveConfig(false)}>
          <MemberConfig member={member} onClose={() => setActiveConfig(false)} />
        </Popup>
      )}
      <div className={"ScheduleButtons" + (moveLeft ? " move-left" : "") + (moveRight ? " move-right" : "")}>
        <div className="leftmenu-button">
          {type === SCREEN_TEAM_PLAN && !isMember && !isViewOnly && (
            <Button title={t("openMetricsPanel")} onClick={() => dispatch(visual.setMenuVisibility("leftmenu", true))}>
              <FiMenu />
            </Button>
          )}
        </div>

        {type !== SCREEN_SCRUM_VIEW && (
          <DateButtons type={type} isDayView={isDayView} activeDate={activeDate} mondayDate={mondayDate} memberId={memberId} />
        )}
        {type === SCREEN_SCRUM_VIEW && <div className="ScheduleButtons-filler"> </div>}

        <div className="center-buttons">
          {isInPlanningMode || (type === SCREEN_TEAM_PLAN && !isMember) ? <PlanningModeButtons /> : null}

          {showLibrarian && !isViewOnly && (
            <Button variant="primary" onClick={handleLibrarianNavigation}>
            {t("qcLibrarian")}
          </Button>
          )}
          <Button variant="primary" onClick={() => navigate(isMember && directToMemberPlan ? `/member/${memberId}` : '/')}>
            <FiHome title={t("home")} />
          </Button>
          {/* The type condition is to hide the button if the settings-view is already shown */}
          {!isMember && !isViewOnly && !isOnMobile &&
            <Button variant="primary"  onClick={() => navigate("/setup/members")}>
              <FiSettings title={t("settings")} />
            </Button>
          }

          <DropdownButton
            title={
              (() => {
                const firstSegment = location.pathname.split('/')[1];
                const matchingOption = dropDownOptions.find(option => option.url.includes(`/${firstSegment}`));
                return matchingOption
                  ? matchingOption.name
                  : isCreatingProject
                    ? t("creatingProject")
                    : isEditingProject
                      ? t("editingProject")
                      : t("settings");
              })()
            }
            onSelect={(view) => navigate(view)}
          >
            {dropDownOptions.map((view) => {
              return (
                <Dropdown.Item key={view.url} active={view === location.pathname} eventKey={view.url}>
                  {view.name}
                </Dropdown.Item>
              )
            })}
          </DropdownButton>

          <UpdateIndicator/>

        </div>

        <div className="member-planningmode-week-buttons">
          {(type === SCREEN_ASSET_VIEW || type === SCREEN_TEAM_PLAN) && (
            <Button
              title={showCompact ? t("viewNormal") : t("viewCompact")}
              onClick={() => {
                if (screenKey) {
                  dispatch(visual.setScreenShowCompact(screenKey, !showCompact))
                } else {
                  dispatch(visual.setShowCompact(!showCompact))
                }
              }}
            >
              {showCompact ? <TbArrowsMaximize/> : <TbArrowsMinimize/>}
            </Button>
          )}

          {type !== "sample-view" && type !== "project-view" && view !== "day-view" && type !== SCREEN_SCRUM_VIEW && type !== "settings-view" && (
            <Button
              title={withWeek ? t("hideNextWeek") : t("showNextWeek")}
              onClick={() => {
                if (screenKey) {
                  dispatch(visual.setScreenExtraWeekVisibility(screenKey, !withWeek))
                } else {
                  dispatch(visual.setExtraWeekVisibility(!withWeek))
                }
              }}
            >
              {withWeek ? t("oneWeek") : t("twoWeeks")}
            </Button>
          )}

          {type !== SCREEN_ASSET_VIEW &&
            type !== "sample-view" &&
            type !== "project-view" &&
            view !== "day-view" &&
            type !== SCREEN_SCRUM_VIEW &&
            type !== "settings-view" && (
              <Button
                title={withWeekend ? t("hideSatSun") : t("showSatSun")}
                onClick={() => {
                  if (screenKey) {
                    dispatch(visual.setScreenWeekendVisibility(screenKey, !withWeekend))
                  } else {
                    dispatch(visual.setWeekendVisibility(!withWeekend))
                  }
                }}
              >
                {withWeekend ? t("hideSatSun") : t("showSatSun")}
              </Button>
            )}
          {type === SCREEN_ASSET_VIEW && (
            <Button
            title={showWeekendAssetView ? t("hideSatSun") : t("showSatSun")}
            onClick={() => dispatch(visual.setShowWeekendAssetview(!showWeekendAssetView))}
          >
            {showWeekendAssetView ? t("hideSatSun") : t("showSatSun")}
          </Button>
          )}

          {member && (
            <Button title={t("configureMember")} id="config-member" onClick={() => setActiveConfig(true)}>
              <RiUserSettingsLine />
            </Button>
          )}

          {showAnalyses && !showProjects && <HelpButton area="analysis" />}
          {showProjects && !showAnalyses && <HelpButton area="project" />}

          {showProjects && showAnalyses && (
            <Dropdown>
              <Dropdown.Toggle variant="primary" id="dropdown-basic" title={t("help")}>
                ?
              </Dropdown.Toggle>
              <Dropdown.Menu className="Schedule-help-dropdown">
                <Dropdown.Item onClick={() => navigate("/help/analysis")}>
                  {t("analysis")}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => navigate("/help/project")}>
                  {t("project")}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
          <Button
            variant="primary"
              onClick={() => {
                if (isInPlanningMode && isCurrentPlanner) {
                  dispatch(teamStatus.showLogoutConfirm(true))
                } else {
                  dispatch(auth.logout()).then(() => navigate("/login"))
                }
              }} title={t("logout")}
          >
            <FiLogOut />
          </Button>
        </div>

        <div className="rightmenu-button">
          {(type === SCREEN_TEAM_PLAN || type === "single-member") && !isViewOnly && (!isMember || currentMemberShowOnPlan || allowTaskTransfer) && (
            <Button title={t("openTodoList")} onClick={() => dispatch(visual.setMenuVisibility("rightmenu", true))}>
              <FiMenu />
            </Button>
          )}
        </div>
      </div>
    </>
  )
}
